/**
 * Contains all annotation rendering and manipulation logic.
 */

import { ApiBounds, ApiConnection, ApiFragmentNode } from "../../shared/interfaces";
import { getCookie } from "../../util/cookie";
import { PrimeQueryKeys } from "../../shared/constants";
import { WorkflowApi } from "../../api/workflow";
import { getIdForBounds } from "../../util/objectUtil";
import { analyticsEvent, AnalyticsEvents } from "../../util/analytics";

const ReferenceCssClass = "referenceHighlight";
const FragmentNodeCssClass = "fragmentNode";
const ConnectionCssClass = "connection";
const ConnectionTooltipCssClass = "connectionTooltip";
const EmbeddingTooltipCssClass = "embeddingTooltip";

const MIN_Z_INDEX = 300;
const MAX_Z_INDEX = 900;
const Z_RANGE = MAX_Z_INDEX - MIN_Z_INDEX;

const createAndAppendEmbeddingTooltip = (
    node: ApiFragmentNode,
    fragmentElement: HTMLElement,
): HTMLDivElement | null => {
    const tooltipId = `embedding-tooltip-${node.id}`;
    const existingElement = document.getElementById(tooltipId);
    if (existingElement) return null;

    const annotContainer = getAnnotationContainerForPage(node.bounds.page);
    if (!annotContainer) return null;

    const tooltip = document.createElement("div");
    tooltip.id = tooltipId;
    tooltip.style.position = "absolute";

    const fragmentRect = fragmentElement.getBoundingClientRect();
    const containerRect = annotContainer.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate available space in each direction
    const spaceAbove = fragmentRect.top - containerRect.top;
    const spaceBelow = containerRect.bottom - fragmentRect.bottom;
    const spaceRight = containerRect.right - fragmentRect.right;

    // Determine best position based on available space
    let position: "top" | "bottom" | "left" | "right";

    if (spaceBelow >= 120 && fragmentRect.bottom < viewportHeight - 150) {
        position = "bottom";
        tooltip.style.top = `${node.bounds.yMax * 100}%`;
        tooltip.style.left = `${node.bounds.xMin * 100}%`;
    } else if (spaceAbove >= 120) {
        position = "top";
        tooltip.style.top = `${node.bounds.yMin * 100}%`;
        tooltip.style.left = `${node.bounds.xMin * 100}%`;
    } else if (spaceRight >= 200 && fragmentRect.right < viewportWidth - 220) {
        position = "right";
        tooltip.style.top = `${node.bounds.yMin * 100}%`;
        tooltip.style.left = `${node.bounds.xMax * 100}%`;
    } else {
        position = "left";
        tooltip.style.top = `${node.bounds.yMin * 100}%`;
        tooltip.style.left = `${node.bounds.xMin * 100}%`;
    }

    tooltip.dataset.position = position;

    tooltip.className = EmbeddingTooltipCssClass;
    annotContainer.appendChild(tooltip);
    return tooltip;
};

const shouldShowEmbeddingText = getCookie(PrimeQueryKeys.showEmbeddingTextCookie) === "true";

const createElementForBounds = (bounds: ApiBounds): HTMLDivElement => {
    const newElement = document.createElement("div");
    newElement.style.position = "absolute";
    newElement.style.left = bounds.xMin * 100 + "%";
    newElement.style.top = bounds.yMin * 100 + "%";
    newElement.style.width = (bounds.xMax - bounds.xMin) * 100 + "%";
    newElement.style.height = (bounds.yMax - bounds.yMin) * 100 + "%";

    // Set the zIndex based on the area of the annotation to make sure that users can interact with
    // smaller annotations that are inside larger ones.
    const area = (bounds.xMax - bounds.xMin) * (bounds.yMax - bounds.yMin);
    // Larger area → lower zIndex
    const raw = MAX_Z_INDEX - Z_RANGE * area;
    const rounded = Math.round(raw);
    // Clamp into [MIN_Z_INDEX - MAX_Z_INDEX]
    newElement.style.zIndex = Math.max(MIN_Z_INDEX, Math.min(MAX_Z_INDEX, rounded)).toString();
    return newElement;
};

const createAndAppendAnnotation = (
    id: string,
    bounds: ApiBounds,
    className: string,
    clickHandler: (() => void) | null = null,
): HTMLDivElement | null => {
    // If the element already exists in the DOM, skip
    const existingElement = document.getElementById(id);
    if (existingElement) {
        return null;
    }

    // Get the container for the given page
    const annotContainer = getAnnotationContainerForPage(bounds.page);
    if (!annotContainer) return null;

    // Create a new <div> for the given bounds
    const newElement = createElementForBounds(bounds);
    newElement.id = id;
    newElement.classList.add(className);

    if (clickHandler) {
        newElement.addEventListener("click", clickHandler);
    }

    // Append to container
    annotContainer.appendChild(newElement);

    return newElement;
};

const createAndAppendConnectionTooltip = (connection: ApiConnection): HTMLDivElement | null => {
    const tooltipId = `callout-tooltip-${getIdForBounds(connection.srcBounds)}`;
    const existingElement = document.getElementById(tooltipId);
    if (existingElement) {
        return null;
    }

    const annotContainer = getAnnotationContainerForPage(connection.srcBounds.page);
    if (!annotContainer) {
        return null;
    }

    const tooltip = document.createElement("div");
    tooltip.id = tooltipId;
    tooltip.style.left = connection.srcBounds.xMin * 100 + "%";
    tooltip.style.top = connection.srcBounds.yMax * 100 + "%";
    tooltip.textContent = connection.description;
    tooltip.className = ConnectionTooltipCssClass;

    annotContainer.appendChild(tooltip);
    return tooltip;
};

/**
 * Get the annotation container for the given page.
 * @param page if one exists, null otherwise (file or page not loaded yet)
 */
export const getAnnotationContainerForPage = (page: number): HTMLElement | null =>
    Array.from(document.getElementsByClassName("fv__pdf-page-annots-container")).find(
        (layer: Element) => layer.parentElement?.getAttribute("data-index") === page.toString(),
    ) as HTMLElement | null;

/**
 * Makes the selected fragment node active.
 * @param selectedFragmentNodeId
 */
export const setAnnotationActive = (selectedFragmentNodeId: string | null): void => {
    const allFragmentNodes = Array.from(
        document.getElementsByClassName(FragmentNodeCssClass),
    ) as HTMLElement[];
    for (const el of allFragmentNodes) {
        if (!el) {
            continue;
        }

        if (el.id === selectedFragmentNodeId) {
            el.classList.add("active");
        } else {
            el.classList.remove("active");
        }
    }
};

/**
 * Finds the fragmentNode element on the same page whose bounds fully contain the given connection bounds.
 * @param bounds The ApiBounds of the connection’s source.
 * @returns The matching fragmentNode HTMLElement, or null if none found.
 */
/**
 * Finds the fragmentNode element on the same page whose bounds
 * fully contain the given connection bounds, with a little tolerance.
 */
function findFragmentNodeForBounds(bounds: ApiBounds, margin = 0.005): HTMLElement | null {
    const container = getAnnotationContainerForPage(bounds.page);
    if (!container) return null;

    // Tell TypeScript that every item in the collection is an HTMLElement
    const elements = Array.from(
        container.getElementsByClassName(FragmentNodeCssClass),
    ) as HTMLElement[];

    const fragment = elements.find((element) => {
        // parse normalized coords
        const left = parseFloat(element.style.left) / 100;
        const top = parseFloat(element.style.top) / 100;
        const width = parseFloat(element.style.width) / 100;
        const height = parseFloat(element.style.height) / 100;

        const xMinAllowed = left - margin;
        const xMaxAllowed = left + width + margin;
        const yMinAllowed = top - margin;
        const yMaxAllowed = top + height + margin;

        return (
            bounds.xMin >= xMinAllowed &&
            bounds.xMax <= xMaxAllowed &&
            bounds.yMin >= yMinAllowed &&
            bounds.yMax <= yMaxAllowed
        );
    });

    return fragment ?? null; // HTMLElement | null
}

/**
 * Renders all annotations onto the PDF viewer.
 *
 * This function displays reference highlights and fragment nodes by appending
 * corresponding DOM elements into the annotation container of each page.
 *
 * @param params - An object containing the parameters for annotation rendering.
 * @param params.refHighlights - An array of bounds representing reference highlights.
 * @param params.fragmentNodes - An array of fragment nodes to render.
 * @param params.connections - An array of connections to render.
 * @param params.selectedFragmentNodeId - The ID of the currently selected fragment node, or null if none.
 * @param params.handleFragmentNodeClick - A callback invoked when a fragment node is clicked.
 */
export function renderAllAnnotations({
    refHighlights,
    fragmentNodes,
    connections,
    selectedFragmentNodeId,
    handleFragmentNodeClick,
    handleConnectionClick,
}: {
    refHighlights: ApiBounds[];
    fragmentNodes: ApiFragmentNode[];
    connections: ApiConnection[];
    selectedFragmentNodeId: string | null;
    handleFragmentNodeClick: (node: ApiFragmentNode) => void;
    handleConnectionClick: (connection: ApiConnection) => void;
}) {
    const visibleIds = new Set(refHighlights.map(getIdForBounds));

    toggleReferenceHighlights(visibleIds);
    renderReferenceHighlights(refHighlights);

    fragmentNodes.forEach((node) => {
        const isActive = node.id === selectedFragmentNodeId;
        const el = renderFragmentNode(node, isActive, () => handleFragmentNodeClick(node));
        if (!el) return;

        if (shouldShowEmbeddingText) {
            attachEmbeddingTooltip(el, node);
        }
    });

    connections.forEach((connection) => {
        renderConnection(connection, () => {
            handleConnectionClick(connection);
        });
    });
}

function toggleReferenceHighlights(visibleIds: Set<string>) {
    document.querySelectorAll<HTMLElement>(".referenceHighlight").forEach((el) => {
        el.classList.toggle("hidden", !visibleIds.has(el.id));
    });
}

function renderReferenceHighlights(refHighlights: ApiBounds[]) {
    refHighlights.forEach((bounds) => {
        const id = getIdForBounds(bounds);
        createAndAppendAnnotation(id, bounds, ReferenceCssClass);
    });
}

function attachConnectionHoverBehavior(element: HTMLElement, bounds: ApiBounds) {
    element.addEventListener("mouseenter", () => {
        const fragEl = findFragmentNodeForBounds(bounds);
        if (fragEl) {
            fragEl.classList.add("hovered");
        }
    });
    element.addEventListener("mouseleave", () => {
        const fragEl = findFragmentNodeForBounds(bounds);
        if (fragEl) {
            fragEl.classList.remove("hovered");
        }
    });
}

function renderFragmentNode(
    node: ApiFragmentNode,
    isActive: boolean,
    onClick: () => void,
): HTMLElement | null {
    // Use baseClass only for the initial class, active state will be handled by classList
    const element = createAndAppendAnnotation(node.id, node.bounds, FragmentNodeCssClass, onClick);
    if (element && isActive) {
        element.classList.add("active");
    }
    return element;
}

function renderConnection(connection: ApiConnection, onClick: () => void): HTMLElement | null {
    const id = getIdForBounds(connection.srcBounds);
    const newBounds = {
        page: connection.srcBounds.page,
        xMin: connection.srcBounds.xMin,
        xMax: connection.srcBounds.xMax,
        yMin: connection.srcBounds.yMin,
        yMax: connection.srcBounds.yMax,
    };
    const element = createAndAppendAnnotation(id, newBounds, ConnectionCssClass, onClick);
    if (!element) {
        return null;
    }
    attachConnectionHoverBehavior(element, connection.srcBounds);

    const tooltip = createAndAppendConnectionTooltip(connection);
    if (!tooltip) {
        return element;
    }

    attachConnectionHoverBehavior(tooltip, connection.srcBounds);
    withTooltipBehavior(element, tooltip);
    element.addEventListener("mouseover", () => analyticsEvent(AnalyticsEvents.CONNECTION_HOVER));

    return element;
}

// TODO: let's extract this into a separate component once we get callouts rolled out
function withTooltipBehavior(
    triggerEl: HTMLElement,
    tooltipEl: HTMLElement,
    loadContent?: () => Promise<string>,
) {
    let hoverTrigger = false;
    let hoverTooltip = false;
    let timeoutId: number;

    const show = () => {
        clearTimeout(timeoutId);
        tooltipEl.style.display = "block";
        tooltipEl.style.opacity = "1";
    };

    const hide = () => {
        if (!hoverTrigger && !hoverTooltip) {
            tooltipEl.style.opacity = "0";
            timeoutId = window.setTimeout(() => {
                if (!hoverTrigger && !hoverTooltip) {
                    tooltipEl.style.display = "none";
                }
            }, 150);
        }
    };

    triggerEl.addEventListener("mouseenter", () => {
        hoverTrigger = true;
        show();
        if (loadContent && !tooltipEl.dataset.loaded) {
            tooltipEl.dataset.loaded = "1";
            loadContent()
                .then((html) => {
                    tooltipEl.innerHTML = html;
                })
                .catch(() => {
                    tooltipEl.textContent = "Error loading content.";
                });
        }
    });

    triggerEl.addEventListener("mouseleave", () => {
        hoverTrigger = false;
        hide();
    });

    tooltipEl.addEventListener("mouseenter", () => {
        hoverTooltip = true;
        show();
    });

    tooltipEl.addEventListener("mouseleave", () => {
        hoverTooltip = false;
        hide();
    });
}

function attachEmbeddingTooltip(el: HTMLElement, node: ApiFragmentNode) {
    const tooltip = createAndAppendEmbeddingTooltip(node, el);
    if (!tooltip) return;

    // Mark the tooltip content container so CSS styles apply
    tooltip.classList.add("embedding-tooltip-content");

    withTooltipBehavior(el, tooltip, () => WorkflowApi.getFragmentNodeEmbeddingText(node.id));
}
